<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import Chip from '@uiKit/elements/Chip.vue'
import Card from '@uiKit/components/Card.vue'
import {
  countdownUntil,
  formatCountdown,
  fundingWindowProgress,
  ONE_MINUTE_MS,
  resolveFundingWindow,
  type FundingWindowFields,
  type FundingWindowPhase,
  type FundingWindowState,
} from '@shared/utils'

// The seller's funding-window countdown. Lives in this module because the clock
// starts on a listing request; the property module imports it for the record the
// request becomes, so both read identically.
//
// Its own rail section rather than a block inside the status card: the window is a
// separate fact from where the request sits in the pipeline, and nesting a boxed
// countdown inside another boxed card read as one card wearing two borders.
//
// Read-only by design. An extension is an agreement admin issues rather than
// something a seller self-serves; this card reports the days it added and never asks
// the seller to act.
//
// `phase` is for the property, which shows its window from setup to settlement:
// faded before go-live, stopped once funded or not. A request leaves it at
// `running`, where only the server's deadline flags decide the state.
const props = withDefaults(
  defineProps<{
    window: FundingWindowFields
    phase?: FundingWindowPhase
    /** Days approved extensions added — the "Extended +N days" tag. */
    extensionDays?: number
    /** Where an extension proposal stands, when one is open. */
    notice?: string | null
  }>(),
  { phase: 'running', extensionDays: 0, notice: null },
)

const presentation = computed(() => resolveFundingWindow(props.window, props.phase))

// The window's dates only mean something while the raise is (or was just) running.
const CLOCKED_STATES: ReadonlySet<FundingWindowState> = new Set(['open', 'urgent', 'lapsed'])
const isClocked = computed(() => CLOCKED_STATES.has(presentation.value.state))

const extensionLabel = computed(() =>
  props.extensionDays > 0
    ? `Extended +${props.extensionDays} day${props.extensionDays === 1 ? '' : 's'}`
    : '',
)

// Ticks the display, nothing else. The state (open / urgent / lapsed) stays fixed
// on the server's own flags for the life of the query, so a device with a skewed
// clock can drift the digits but never flip the card into the wrong state.
const now = ref(Date.now())
let tick: ReturnType<typeof setInterval> | undefined

// One minute, because minutes are the finest unit displayed — a shorter interval
// would re-render without ever changing a digit.
onMounted(() => {
  if (!presentation.value.hasLiveCountdown) return
  tick = setInterval(() => {
    now.value = Date.now()
  }, ONE_MINUTE_MS)
})

onBeforeUnmount(() => {
  if (tick) clearInterval(tick)
})

const countdown = computed(() => {
  const { fundingDeadlineAt } = props.window
  if (!fundingDeadlineAt || !presentation.value.hasLiveCountdown) return null
  return formatCountdown(countdownUntil(fundingDeadlineAt, now.value))
})

// Days burnt, not days left — the countdown already says the latter. Together they
// answer "how long have I got" and "how far in am I", which is the question a seller
// asks once the number stops feeling abstract.
//
// Measured from go-live, so the bar starts at zero the day the listing opens rather
// than counting the onboarding and compliance time that ran before it.
const progress = computed(() =>
  isClocked.value
    ? fundingWindowProgress(props.window.goLiveAt, props.window.fundingDeadlineAt, now.value)
    : null,
)

const ICON_BY_STATE: Partial<Record<FundingWindowState, PiIconName>> = {
  lapsed: 'times-circle',
  notFunded: 'times-circle',
  funded: 'check-circle',
}
const icon = computed<PiIconName>(() => ICON_BY_STATE[presentation.value.state] ?? 'clock')

const elapsedLabel = computed(() =>
  progress.value ? `${Math.round(progress.value.percentElapsed)}% used` : '',
)
</script>

<template>
  <Card
    v-if="presentation.isVisible"
    is="section"
    variant="outlined"
    padding="6"
    direction="col"
    gap="4"
    :class="['window', `window--${presentation.tone}`, presentation.isFaded && 'window--faded']"
  >
    <Box flex align="start" gap="3">
      <Box flex align="center" justify="center" class="window__icon-wrap">
        <PiIcon :name="icon" class="window__icon" />
      </Box>
      <Box direction="col" gap="1" grow class="window__copy">
        <Box flex align="center" gap="2" class="window__title-row">
          <Text is="h3" type="page-title" weight="semibold">{{ presentation.title }}</Text>
          <Chip
            v-if="extensionLabel"
            :label="extensionLabel"
            color="primary"
            variant="outline"
            size="small"
          />
        </Box>
        <Text type="caption" color="muted">{{ presentation.message }}</Text>
        <Text v-if="notice" type="caption" weight="medium" color="default">{{ notice }}</Text>
      </Box>
    </Box>

    <Box v-if="countdown || progress" direction="col" gap="3" class="window__clock">
      <Box v-if="countdown" direction="col" gap="1" align="center">
        <Text is="span" type="micro">Time remaining</Text>
        <Text type="display" weight="semibold" class="window__digits">{{ countdown }}</Text>
      </Box>

      <Box v-if="progress" direction="col" gap="2">
        <Box class="window__track">
          <Box class="window__fill" :style="{ width: `${progress.percentElapsed}%` }" />
        </Box>
        <Box flex align="center" justify="between" gap="2">
          <Text type="caption-sm" weight="medium">
            Day {{ progress.elapsedDays }} of {{ progress.totalDays }}
          </Text>
          <Text type="caption-sm" color="muted">{{ elapsedLabel }}</Text>
        </Box>
      </Box>
    </Box>
  </Card>
</template>

<style scoped>
/* Card supplies the radius and the white ground; the tone drives everything else.
   Each state paints from one ramp — purple while the window is simply running, amber
   in the final stretch, red once it has closed — so the card reads as live rather
   than as a disabled panel. */
.window {
  border: 1px solid;
}

.window__copy {
  min-width: 0;
}

.window__title-row {
  flex-wrap: wrap;
  row-gap: 0.25rem;
}

/* The window exists but has not started: present, but plainly not running. */
.window--faded {
  opacity: 0.6;
}

.window__icon-wrap {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
}

.window__icon {
  font-size: 0.875rem;
}

.window__clock {
  padding: 1rem;
  border-radius: var(--p-border-radius-lg);
  border: 1px solid;
}

/* Tabular figures so the minutes tick without the row reflowing, and never wrapped:
   "84D : 02H : 29M" broken across two lines stops reading as one duration. The rail
   is narrow, so the size is set here rather than taken from a larger Text preset. */
.window__digits {
  font-variant-numeric: tabular-nums;
  font-size: 1.375rem;
  line-height: 1.2;
  white-space: nowrap;
}

/* Window consumed, not funding raised — the fill grows as time runs out, which is
   why it darkens toward the tone's strong end rather than staying flat. */
.window__track {
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
}

.window__fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

@media (prefers-reduced-motion: reduce) {
  .window__fill {
    transition: none;
  }
}

/* ── Running ────────────────────────────────────────────────────────────── */
.window--info {
  border-color: var(--p-primary-200);
}

.window--info .window__icon-wrap {
  background: var(--p-primary-100);
  color: var(--p-primary-600);
}

.window--info .window__clock {
  background: var(--p-primary-50);
  border-color: var(--p-primary-100);
}

.window--info .window__digits {
  color: var(--p-primary-700);
}

.window--info .window__track {
  background: var(--p-primary-100);
}

.window--info .window__fill {
  background: var(--p-primary-600);
}

/* ── Final stretch ──────────────────────────────────────────────────────── */
.window--warning {
  border-color: var(--p-warning-300);
}

.window--warning .window__icon-wrap {
  background: var(--p-warning-100);
  color: var(--p-warning-700);
}

.window--warning .window__clock {
  background: var(--p-warning-50);
  border-color: var(--p-warning-200);
}

.window--warning .window__digits {
  color: var(--p-warning-700);
}

.window--warning .window__track {
  background: var(--p-warning-200);
}

.window--warning .window__fill {
  background: var(--p-warning-500);
}

/* ── Fully funded ───────────────────────────────────────────────────────── */
.window--success {
  border-color: var(--p-success-200);
}

.window--success .window__icon-wrap {
  background: var(--p-success-100);
  color: var(--p-success-600);
}

/* ── Closed ─────────────────────────────────────────────────────────────── */
.window--danger {
  border-color: var(--p-danger-200);
}

.window--danger .window__icon-wrap {
  background: var(--p-danger-100);
  color: var(--p-danger-600);
}

.window--danger .window__clock {
  background: var(--p-danger-50);
  border-color: var(--p-danger-200);
}

.window--danger .window__track {
  background: var(--p-danger-200);
}

.window--danger .window__fill {
  background: var(--p-danger-500);
}
</style>
